import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Text } from 'rebass';
import { Input, Button, CheckBox, theme, Icon, Tag, Typography } from '@lomadee/design-system';
import { useMediaQuery } from 'react-responsive';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  GetBrazilBanksResponse,
  GET_BRAZIL_BANKS,
  GetBrazilBanksVariables,
} from '@plug/domains/organization/domains/affiliate/graphql/queries/get-brazil-banks';

import { Spacer } from '@plug/ui';
import { MARY_KAY_CODE_BANKS, MARY_KAY_WHITELABEL, OLIST_HOST_NAME } from '@plug/config/consts';
import { Flex } from '@plug/ui2/rebass';
import { Modal } from '@material-ui/core';
import { useTerms } from '@plug/hooks/use-terms';
import { useCreateAccountContext } from '@plug/contexts/CreateAccountContext';
import { BankAccountType } from '@plug/domains/organization/domains/affiliate/contexts/AccountContext';
import { RequiredBankInformationSchema } from './AffiliateRegistrationBankInformation.schema';
import { TitleForm } from '../../style';

interface AffiliateRegistrationBankInformationStepI {
  handleCreateAccount: (values: CreateAccountFormI) => void;
}

const AffiliateRegistrationBankInformationStep: React.FC<AffiliateRegistrationBankInformationStepI> = ({
  handleCreateAccount,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const [showModal, setShowModal] = useState(false);

  const { customTerm, term } = useTerms();

  const isOlist = OLIST_HOST_NAME.includes(window.location.hostname);
  const isMaryKay = MARY_KAY_WHITELABEL.includes(window.location.hostname);

  const MERCADO_PAGO_ID = 'df38ba5a-8c90-4ba8-b3cc-5fead1e6b9fb';

  const {
    values: contextValues,
    addValues,
    setStep,
    blockInitialValues,
    role,
    isCreatingAccount,
  } = useCreateAccountContext();

  const { data: getBrazilBanksData, loading } = useQuery<
    GetBrazilBanksResponse,
    GetBrazilBanksVariables
  >(GET_BRAZIL_BANKS, {
    variables: {
      input: {
        ...(isMaryKay && { codes: MARY_KAY_CODE_BANKS }),
      },
    },
  });

  const banks = useMemo(() => {
    if (getBrazilBanksData?.getBrazilBanks.length) {
      return getBrazilBanksData?.getBrazilBanks.map(({ id, name, code }) => ({
        label: `${code} - ${name}`,
        value: id,
      }));
    }
    return [];
  }, [getBrazilBanksData]);

  const handleSubmitFormik = (formikValues: CreateAccountFormI): void => {
    addValues(formikValues);
    handleCreateAccount(formikValues);
  };

  const handleBackToPasswordStep = (): void => {
    setStep('PERSONAL_DATA');
  };

  const accountTypeRelation: { [key in BankAccountType]: string } = {
    checking: t(
      'hubly-dashboard.signup.affiliate.right-side.third-step.form.account-type.options.checking',
    ),
    savings: t(
      'hubly-dashboard.signup.affiliate.right-side.third-step.form.account-type.options.saving',
    ),
  };

  const getPageTitle = () => {
    if (MARY_KAY_WHITELABEL.includes(window.location.hostname)) {
      return (
        <div>
          Quase lá! <br />
          Cadastre sua conta bancária para receber o ganho de suas vendas online.
        </div>
      );
    }
    if (OLIST_HOST_NAME.includes(window.location.hostname)) {
      return 'Cadastre a conta bancária vinculada ao CNPJ cadastrado na etapa anterior, para receber comissões.';
    }
    return t('hubly-dashboard.signup.affiliate.right-side.third-step.title');
  };

  const pageTitle = getPageTitle();

  const validationSchema = Yup.object().shape({
    ...RequiredBankInformationSchema.fields,
    bank: Yup.string().test(
      'isValidBank',
      'Por favor, selecione seu banco a partir das opções disponíveis',
      function(value) {
        const validBankIds = banks.map(bank => bank.value);
        return validBankIds.includes(value);
      },
    ),
    termsAndPolitics: Yup.boolean()
      .oneOf([true], 'É preciso aceitar os termos para continuar.')
      .required(),
  });

  return (
    <Flex flexDirection="column" sx={{ gap: '16px' }}>
      {/* modal termos */}
      <Modal open={showModal}>
        <Flex width="100%" height="100%" justifyContent="center" alignItems="center">
          <Flex
            width="98vw"
            height="100%"
            maxHeight="98vh"
            justifyContent="center"
            backgroundColor="white"
            overflow="hidden"
            flexDirection="column"
            marginX="auto"
            paddingX="16px"
            style={{ gap: '20px' }}
          >
            <Flex
              paddingY="10px"
              alignItems="center"
              justifyContent="flex-end"
              style={{ cursor: 'pointer' }}
            >
              <Icon iconId="close" stroke="neutralLow" onClick={() => setShowModal(false)} />
            </Flex>

            {customTerm && (
              <Box
                flex="1"
                backgroundColor="white"
                overflowY="scroll"
                dangerouslySetInnerHTML={{ __html: customTerm }}
              />
            )}

            {!MARY_KAY_WHITELABEL.includes(window.location.hostname) && (
              <Box
                flex="1"
                backgroundColor="white"
                overflowY="scroll"
                dangerouslySetInnerHTML={{ __html: term }}
              />
            )}
          </Flex>
        </Flex>
      </Modal>

      {!isMobile && <Spacer mt="48px" />}

      <TitleForm>{pageTitle}</TitleForm>

      {MARY_KAY_WHITELABEL.includes(window.location.hostname) && (
        <>
          <Flex
            alignItems="center"
            justifyContent="center"
            backgroundColor={'#faedef'}
            borderRadius="8px"
            p={'16px'}
          >
            <Typography
              className="items-center"
              size="14"
              lineHeight="18"
              color="textNeutralDefault"
              fontWeight={400}
            >
              <Typography size="14" lineHeight="18" color="textNeutralDefault" fontWeight={600}>
                IMPORTANTE:
              </Typography>{' '}
              Antes de cadastrar, verifique se o seu banco possui a modalidade de pix ativa, para
              receber os ganhos de suas vendas digitais.
              <br /> Para mais detalhes clique e acesse o{' '}
              <a
                href="https://mk.marykayintouch.com.br/s/login/?language=pt_BR&ec=302&startURL=%2Fs%2Farticle%2FMary-Kay-a-Um-Clique-FAQ"
                target="_blank"
                style={{ color: '#d00070', fontWeight: 700 }}
                rel="noreferrer"
              >
                FAQ Mary Kay a Um Clique
              </a>
              .
            </Typography>
          </Flex>
        </>
      )}

      <Formik
        initialValues={{ ...contextValues, termsAndPolitics: false }}
        validationSchema={validationSchema}
        validateOnChange
        validateOnBlur
        onSubmit={handleSubmitFormik}
      >
        {({
          values,
          handleChange,
          submitForm,
          handleBlur,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
        }): JSX.Element => {
          const handleSkip = (): void => {
            if (!values.termsAndPolitics) {
              setFieldTouched('termsAndPolitics', true, true);
              return;
            }

            addValues({ termsAndPolitics: values.termsAndPolitics });

            handleCreateAccount({
              ...contextValues,
              termsAndPolitics: values.termsAndPolitics,
            });
          };

          return (
            <>
              {MERCADO_PAGO_ID === values.bank &&
                MARY_KAY_WHITELABEL.includes(window.location.hostname) && (
                  <Tag hierarchy="warning" fluid>
                    <Flex gap="16px" alignItems="center" p={'8px 12px'}>
                      <Icon iconId="alert" stroke={'warningDark'} size="MD" strokeWidth={2} />{' '}
                      <span style={{ textAlign: 'start' }}>
                        ATENÇÃO: No Banco Mercado Pago são aceitos apenas cartões Visa e Mastercard.
                        Para a bandeira Elo (crédito ou débito), selecione outro domicílio bancário.
                      </span>
                    </Flex>
                  </Tag>
                )}

              {!isMobile && (
                <>
                  <Flex sx={{ gap: '16px' }}>
                    <Flex flex={1.5}>
                      <Input
                        label={
                          isOlist
                            ? 'Banco'
                            : t(
                                'hubly-dashboard.signup.affiliate.right-side.third-step.form.bank.label',
                              )
                        }
                        placeholder={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.bank.placeholder',
                        )}
                        onClickDropdownItem={clickedValue => setFieldValue('bank', clickedValue)}
                        dropdown={banks}
                        name="bank"
                        value={
                          banks.find(({ value }) => value === values.bank)?.label ?? values.bank
                        }
                        onChange={e => {
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        error={touched.bank && !!errors.bank}
                        helperText={touched.bank ? errors.bank : ''}
                        fluid
                        iconRight="chevronDown"
                        iconRightSize="XS"
                        disabled={loading || isCreatingAccount}
                        filterOptions
                      />
                    </Flex>

                    <Flex flex={1}>
                      <Input
                        label={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.account-type.label',
                        )}
                        placeholder={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.account-type.placeholder',
                        )}
                        name="bankType"
                        onClickDropdownItem={clickedValue =>
                          setFieldValue('bankType', clickedValue)
                        }
                        dropdown={[
                          {
                            label: 'Conta corrente',
                            value: 'checking',
                          },
                          {
                            label: 'Conta poupança',
                            value: 'savings',
                          },
                        ]}
                        value={
                          values.bankType
                            ? accountTypeRelation[
                                values.bankType as keyof typeof accountTypeRelation
                              ] ?? values.bankType
                            : ''
                        }
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={touched.bankType && !!errors.bankType}
                        helperText={touched.bankType ? errors.bankType : ''}
                        fluid
                        iconRight="chevronDown"
                        iconRightSize="XS"
                        disabled={isCreatingAccount}
                      />
                    </Flex>
                  </Flex>

                  <Flex sx={{ gap: '16px' }}>
                    <Flex flex={1.5}>
                      <Input
                        label={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.agency.label',
                        )}
                        placeholder={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.agency.placeholder',
                        )}
                        name="agency"
                        value={values.agency}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={touched.agency && !!errors.agency}
                        helperText={touched.agency ? errors.agency : ''}
                        disabled={isCreatingAccount}
                        fluid
                      />
                    </Flex>

                    <Flex flex={1}>
                      <Input
                        label={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.agencyDigit.label',
                        )}
                        placeholder={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.agencyDigit.placeholder',
                        )}
                        name="agencyDigit"
                        value={values.agencyDigit}
                        onBlur={handleBlur}
                        onChange={e => {
                          e.target.value = e.target.value.replace(/\D/g, '');
                          if (e?.currentTarget?.value)
                            e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '');

                          handleChange(e);
                        }}
                        error={touched.agencyDigit && !!errors.agencyDigit}
                        // helperText={touched.account ? errors.account : ''}
                        maxLength={1}
                        disabled={isCreatingAccount}
                        fluid
                      />
                    </Flex>
                  </Flex>

                  <Flex sx={{ gap: '10px', flexDirection: 'column' }}>
                    <Flex sx={{ gap: '16px' }}>
                      <Flex flex={1.5} flexDirection="column" style={{ gap: '8px' }}>
                        <Input
                          label={t(
                            'hubly-dashboard.signup.affiliate.right-side.third-step.form.account.label',
                          )}
                          placeholder={t(
                            'hubly-dashboard.signup.affiliate.right-side.third-step.form.account.placeholder',
                          )}
                          name="account"
                          value={values.account}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={touched.account && !!errors.account}
                          helperText={touched.account ? errors.account : ''}
                          disabled={isCreatingAccount}
                          fluid
                        />
                      </Flex>

                      <Flex flex={1}>
                        <Input
                          label={t(
                            'hubly-dashboard.signup.affiliate.right-side.third-step.form.accountDigit.label',
                          )}
                          placeholder={t(
                            'hubly-dashboard.signup.affiliate.right-side.third-step.form.accountDigit.placeholder',
                          )}
                          name="accountDigit"
                          value={values.accountDigit}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={touched.accountDigit && !!errors.accountDigit}
                          // helperText={touched.accountDigit ? errors.accountDigit : ''}
                          disabled={isCreatingAccount}
                          maxLength={1}
                          fluid
                        />
                      </Flex>
                    </Flex>

                    {MARY_KAY_WHITELABEL.includes(window.location.hostname) && (
                      <text
                        style={{
                          fontSize: '14px',
                          fontWeight: 400,
                          color: theme.colors.neutralLow,
                          marginBottom: '5px',
                        }}
                      >
                        (A conta bancária deve ser de pessoa física, vinculada ao seu CPF)
                      </text>
                    )}
                  </Flex>
                </>
              )}

              {isMobile && (
                <>
                  <Input
                    label={
                      isOlist
                        ? 'Banco'
                        : t(
                            'hubly-dashboard.signup.affiliate.right-side.third-step.form.bank.label',
                          )
                    }
                    placeholder={t(
                      'hubly-dashboard.signup.affiliate.right-side.third-step.form.bank.placeholder',
                    )}
                    onClickDropdownItem={clickedValue => setFieldValue('bank', clickedValue)}
                    dropdown={banks}
                    name="bank"
                    value={banks.find(({ value }) => value === values.bank)?.label ?? values.bank}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.bank && !!errors.bank}
                    helperText={touched.bank ? errors.bank : ''}
                    fluid
                    iconRight="chevronDown"
                    iconRightSize="XS"
                    disabled={loading || isCreatingAccount}
                    filterOptions
                  />

                  <Input
                    label={t(
                      'hubly-dashboard.signup.affiliate.right-side.third-step.form.account-type.label',
                    )}
                    placeholder={t(
                      'hubly-dashboard.signup.affiliate.right-side.third-step.form.account-type.placeholder',
                    )}
                    name="bankType"
                    onClickDropdownItem={clickedValue => setFieldValue('bankType', clickedValue)}
                    dropdown={[
                      {
                        label: 'Conta corrente',
                        value: 'checking',
                      },
                      {
                        label: 'Conta poupança',
                        value: 'savings',
                      },
                    ]}
                    value={
                      values.bankType
                        ? accountTypeRelation[
                            values.bankType as keyof typeof accountTypeRelation
                          ] ?? values.bankType
                        : ''
                    }
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.bankType && !!errors.bankType}
                    helperText={touched.bankType ? errors.bankType : ''}
                    fluid
                    disabled={isCreatingAccount}
                    iconRight="chevronDown"
                    iconRightSize="XS"
                  />

                  <Flex style={{ gap: '12px' }}>
                    <Flex flex={1}>
                      <Input
                        label={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.agency.label',
                        )}
                        placeholder={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.agency.placeholder',
                        )}
                        name="agency"
                        value={values.agency}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={touched.agency && !!errors.agency}
                        // helperText={touched.agency ? errors.agency : ''}
                        disabled={isCreatingAccount}
                        fluid
                      />
                    </Flex>

                    <Flex flex={1}>
                      <Input
                        label={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.agencyDigit.label',
                        )}
                        placeholder={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.agencyDigit.placeholder',
                        )}
                        name="agencyDigit"
                        value={values.agencyDigit}
                        onBlur={handleBlur}
                        onChange={e => {
                          e.target.value = e.target.value.replace(/\D/g, '');
                          if (e?.currentTarget?.value)
                            e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '');

                          handleChange(e);
                        }}
                        error={touched.agencyDigit && !!errors.agencyDigit}
                        // helperText={touched.account ? errors.account : ''}
                        maxLength={1}
                        disabled={isCreatingAccount}
                        fluid
                      />
                    </Flex>
                  </Flex>

                  <Flex sx={{ gap: '12px' }}>
                    <Flex flex={1}>
                      <Input
                        label={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.account.label',
                        )}
                        placeholder={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.account.placeholder',
                        )}
                        name="account"
                        value={values.account}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={touched.account && !!errors.account}
                        // helperText={touched.account ? errors.account : ''}
                        disabled={isCreatingAccount}
                        fluid
                      />
                    </Flex>

                    <Flex flex={1}>
                      <Input
                        label={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.accountDigit.label',
                        )}
                        placeholder={t(
                          'hubly-dashboard.signup.affiliate.right-side.third-step.form.accountDigit.placeholder',
                        )}
                        name="accountDigit"
                        value={values.accountDigit}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={touched.accountDigit && !!errors.accountDigit}
                        // helperText={touched.accountDigit ? errors.accountDigit : ''}
                        disabled={isCreatingAccount}
                        fluid
                      />
                    </Flex>
                  </Flex>
                </>
              )}

              <Input
                label={t('hubly-dashboard.signup.affiliate.right-side.third-step.form.name.label')}
                placeholder={t(
                  'hubly-dashboard.signup.affiliate.right-side.third-step.form.name.placeholder',
                )}
                name="accountHolderName"
                value={values.accountHolderName}
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.accountHolderName && !!errors.accountHolderName}
                helperText={touched.accountHolderName ? errors.accountHolderName : ''}
                disabled={isCreatingAccount}
                fluid
              />

              {/* checkbox controlado pelo formik */}
              <Flex width="100%" alignItems="center" mt={'10px'}>
                <CheckBox
                  value={values.termsAndPolitics}
                  name="termsAndPolitics"
                  onValueChange={() => setFieldValue('termsAndPolitics', !values.termsAndPolitics)}
                  disabled={isCreatingAccount}
                />
                <Text fontSize="14px" color={theme.colors.neutralLow} size="XXS">
                  Declaro que li e concordo com os{' '}
                  <Text
                    color={theme.colors.primaryMedium}
                    fontSize="14px"
                    as="span"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setShowModal(true)}
                  >
                    Termos de uso e políticas de privacidade
                  </Text>
                </Text>
              </Flex>

              {touched.termsAndPolitics && errors.termsAndPolitics && (
                <Text color="#E53935" fontSize="12px" mt="4px">
                  {errors.termsAndPolitics}
                </Text>
              )}

              <Flex
                width="100%"
                marginTop={'20px'}
                flexDirection="column"
                style={{ gap: isMobile ? '12px' : '16px' }}
              >
                <Button disabled={isCreatingAccount} onClick={submitForm}>
                  {t(
                    'hubly-dashboard.signup.affiliate.right-side.third-step.form.buttons.confirm.text',
                  )}
                </Button>

                <Button
                  hierarchy="tonalFilled"
                  disabled={isCreatingAccount}
                  onClick={handleBackToPasswordStep}
                  icon="chevronLeft"
                >
                  {t(
                    'hubly-dashboard.signup.affiliate.right-side.second-step.form.buttons.back.text',
                  )}
                </Button>

                {!blockInitialValues && (
                  <Button hierarchy="tonalFilled" disabled={isCreatingAccount} onClick={handleSkip}>
                    {t(
                      'hubly-dashboard.signup.affiliate.right-side.third-step.form.buttons.skip.text',
                    )}
                  </Button>
                )}
              </Flex>
            </>
          );
        }}
      </Formik>
    </Flex>
  );
};

export default AffiliateRegistrationBankInformationStep;
